import { Box, Stack, Typography } from '@mui/material'
import type { FC, PropsWithChildren, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { To } from 'react-router'

import { Chip, COLORS, unit } from '@hcr/ui'
import { isNotUndefined } from '@hcr/utils'

import { LocaleNamespace } from '../../../../../models'
import { LayoutNavigationBack, Metadata } from '../../../../common'
import type { AdditionalInfoListItem } from '../additional-info-list'
import { AdditionalInfoList } from '../additional-info-list'
import { TicketDivider } from '../ticket-divider'

import {
  LayoutTicketDetailsInactiveContainer,
  LayoutTicketDetailsInactiveContent,
  LayoutTicketDetailsInactiveFooter,
  LayoutTicketDetailsInactiveHeader,
  LayoutTicketDetailsInactiveName,
} from './LayoutTicketDetailsInactiveComponents'

interface LayoutTicketDetailsInactiveProps extends PropsWithChildren {
  additionalInfo: AdditionalInfoListItem[]
  backNavigationPath: To
  cta: ReactNode
  guests?: string
  guestsLabel?: string
  name: ReactNode
  type: string
  validity?: ReactNode
}

export const LayoutTicketDetailsInactive: FC<LayoutTicketDetailsInactiveProps> = ({
  additionalInfo,
  backNavigationPath,
  children,
  cta,
  guests,
  guestsLabel,
  name,
  type,
  validity,
}) => {
  const { t } = useTranslation()

  return (
    <LayoutNavigationBack to={backNavigationPath} arrowVariant='text'>
      <Metadata title={t('page-title.ticket-details')} />
      <LayoutTicketDetailsInactiveContainer>
        <LayoutTicketDetailsInactiveContent>
          <LayoutTicketDetailsInactiveHeader>
            <LayoutTicketDetailsInactiveName>
              <Stack gap={unit(1)}>
                <Typography variant='labelM' color={COLORS.grey[700]}>
                  {type}
                </Typography>
                <Typography component='h1' variant='headlineM'>
                  {name}
                </Typography>
              </Stack>
              <Box>
                <Chip text={t('Inactive', { ns: LocaleNamespace.TicketStatus })} variant='Inactive' />
              </Box>
            </LayoutTicketDetailsInactiveName>
            <Stack gap={unit(1)}>
              {isNotUndefined(guestsLabel) && (
                <Typography component='span' variant='titleS'>
                  {guestsLabel}
                </Typography>
              )}
              {isNotUndefined(guests) && (
                <Typography component='span' variant='titleS'>
                  {guests}
                </Typography>
              )}
              {isNotUndefined(validity) && (
                <Typography component='span' variant='bodyM'>
                  {validity}
                </Typography>
              )}
            </Stack>
          </LayoutTicketDetailsInactiveHeader>
          {children && <TicketDivider />}
          {children}
        </LayoutTicketDetailsInactiveContent>
        <LayoutTicketDetailsInactiveFooter>
          <AdditionalInfoList items={additionalInfo} />
          {cta}
        </LayoutTicketDetailsInactiveFooter>
      </LayoutTicketDetailsInactiveContainer>
    </LayoutNavigationBack>
  )
}
